import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useHistory, useLocation, Link } from 'react-router-dom';
import {
  Container,
  Loader,
  Segment,
  Header,
  Input,
  Pagination,
  Label,
  Menu,
  Icon,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../api';

const PAGE_SIZE = 10;
const PLATFORMS = ['nick', 'volto', 'volto_slate', 'volto_vlt', 'plone'];

const TranslationBrowser = () => {
  const { platform: urlPlatform } = useParams();
  const history = useHistory();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const initialSearch = searchParams.get('search') || '';
  const initialPage = parseInt(searchParams.get('page'), 10) || 1;
  const initialLanguage = searchParams.get('language') || '';

  const platform = urlPlatform || 'nick';
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState(initialSearch);
  const [searchInput, setSearchInput] = useState(initialSearch);
  const [page, setPage] = useState(initialPage);
  const [language, setLanguage] = useState(initialLanguage);

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set('platform', platform);
    if (search) params.set('search', search);
    if (language) params.set('language', language);
    params.set('page', page);
    params.set('page_size', PAGE_SIZE);

    fetchApi(`${getApiPath()}/@polyglot/messages-browse?${params.toString()}`)
      .then((json) => {
        setItems(json.items || []);
        setTotal(parseInt(json.items_total || 0, 10));
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [platform, search, language, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (language) params.set('language', language);
    if (page > 1) params.set('page', page);
    const qs = params.toString();
    history.replace(`/polyglot/browse/${platform}${qs ? `?${qs}` : ''}`);
  }, [search, language, page, platform, history]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handlePlatformChange = (p) => {
    setPage(1);
    history.push(`/polyglot/browse/${p}`);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <Container id="polyglot-browser" style={{ paddingTop: '2em', paddingBottom: '2em' }}>
      <Helmet title={`${platform} Translations - Polyglot`} />
      <Link to="/polyglot">&larr; Back to Dashboard</Link>

      <div className="polyglot-page-header">
        <h1>Translation Browser</h1>
        <p className="subtitle">
          {total.toLocaleString()} messages on {platform}
        </p>
      </div>

      {/* Platform tabs */}
      <Menu pointing secondary>
        {PLATFORMS.map((p) => (
          <Menu.Item
            key={p}
            name={p}
            active={platform === p}
            onClick={() => handlePlatformChange(p)}
          />
        ))}
      </Menu>

      {/* Search bar */}
      <Segment>
        <div style={{ display: 'flex', gap: '1em', flexWrap: 'wrap', alignItems: 'center' }}>
          <Input
            placeholder="Search English source text..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleSearch();
            }}
            action={{ icon: 'search', onClick: handleSearch }}
            style={{ width: '400px' }}
          />
          <Input
            placeholder="Filter by language (e.g. sv)"
            value={language}
            onChange={(e) => {
              setLanguage(e.target.value.trim());
              setPage(1);
            }}
            icon="filter"
            style={{ width: '220px' }}
          />
          {(search || language) && (
            <Label
              as="a"
              onClick={() => {
                setSearch('');
                setSearchInput('');
                setLanguage('');
                setPage(1);
              }}
              style={{ cursor: 'pointer' }}
            >
              <Icon name="close" /> Clear filters
            </Label>
          )}
        </div>
      </Segment>

      {error && <Segment color="red">Error: {error}</Segment>}

      {loading ? (
        <Loader active inline="centered" content="Loading messages..." />
      ) : (
        <>
          {items.length === 0 && (
            <Segment placeholder textAlign="center">
              <Header icon>
                <Icon name="search" />
                No messages found.
              </Header>
            </Segment>
          )}

          {items.map((msg) => (
            <MessageCard key={msg.message_id} msg={msg} language={language} />
          ))}

          {totalPages > 1 && (
            <div style={{ textAlign: 'center', marginTop: '1.5em' }}>
              <Pagination
                activePage={page}
                totalPages={totalPages}
                onPageChange={(e, { activePage }) => setPage(activePage)}
              />
            </div>
          )}
        </>
      )}
    </Container>
  );
};

const MessageCard = ({ msg, language }) => {
  const translations = msg.translations || [];

  return (
    <Segment style={{ marginBottom: '1em' }}>
      {/* Header: English source */}
      <div style={{ marginBottom: '0.5em' }}>
        <Link
          to={`/polyglot/messages/${msg.message_id}`}
          style={{ fontSize: '1.15em', fontWeight: 'bold', color: '#1a1a1a' }}
        >
          {msg.english_source}
        </Link>
      </div>

      {/* ISO 704 concept definition */}
      {msg.concept && (
        <div style={{ fontSize: '0.9em', color: '#555', marginBottom: '0.6em', fontStyle: 'italic' }}>
          {msg.concept.definition}
          {msg.concept.subject_field && (
            <Label size="mini" style={{ marginLeft: '0.6em', verticalAlign: 'middle' }}>
              {msg.concept.subject_field}
            </Label>
          )}
        </div>
      )}

      {/* Metadata line: ID, domain, msgctxt */}
      <div style={{ fontSize: '0.8em', color: '#aaa', marginBottom: '0.8em', display: 'flex', gap: '0.8em', flexWrap: 'wrap', alignItems: 'center' }}>
        <span>#{msg.message_id}</span>
        <span>{msg.domain}</span>
        {msg.msgctxt && (
          <span title="PO message context (msgctxt)">{msg.msgctxt}</span>
        )}
      </div>

      {/* Translations grid */}
      {translations.length > 0 ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '0.3em 1em',
          fontFamily: 'var(--polyglot-font, system-ui, sans-serif)',
        }}>
          {translations.map((t) => {
            const isMissing = !t.msgstr || t.msgstr === msg.english_source;
            return (
              <div
                key={t.language}
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '0.5em',
                  padding: '0.25em 0',
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                <Link
                  to={`/polyglot/languages/${t.language}`}
                  style={{
                    fontWeight: 'bold',
                    fontSize: '0.85em',
                    color: '#2185d0',
                    minWidth: '3.5em',
                    flexShrink: 0,
                  }}
                >
                  {t.language}
                </Link>
                <span
                  style={{
                    wordBreak: 'break-word',
                    color: isMissing ? '#bbb' : '#333',
                    fontStyle: isMissing ? 'italic' : 'normal',
                    fontSize: '0.92em',
                  }}
                >
                  {isMissing
                    ? t.msgstr || '(empty)'
                    : t.msgstr}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ color: '#999', fontStyle: 'italic', fontSize: '0.9em' }}>
          No translations found
        </div>
      )}
    </Segment>
  );
};

export default TranslationBrowser;
